export const noteTemplates = [
    {
      name: "Project Updates",
      summary: "Project updates template",
      content: "# 📁 Project: [Project Name]\n**Date:** YYYY-MM-DD  \n**Owner:** [Team/Lead Name]  \n**Sprint:** [Sprint #]  \n**Phase:** [Design / Dev / QA / UAT / Released]  \n**Status:** 🟢 On Track | 🟠 At Risk | 🔴 Blocked\n---\n## 🧱 Features\n### 🔹 Feature: [Feature Name]\n```\n- Status: In progress / To-be-started / Blocked / Done  \n- Owner: [Dev/PM Name]  \n- Updates:\n  - [YYYY-MM-DD] [Brief update or note]\n  - [YYYY-MM-DD] [Another update]\n- Notes\n  - Key Note 1\n  - Key Note 2\n  - Key Note 3\n```\n\n---\n## 🔍 Summary\n```\n- Blockers: [Summary]\n- Next Milestone: [Date + Description]\n```",
      blocks: [
        {
          type: "project",
          name: "Project",
          icon: "📁",
          content: "# 📁 Project: [Project Name]\n**Date:** YYYY-MM-DD  \n**Owner:** [Team/Lead Name]  \n**Sprint:** [Sprint #]  \n**Phase:** [Design / Dev / QA / UAT / Released]  \n**Status:** 🟢 On Track | 🟠 At Risk | 🔴 Blocked",
          required: true,
          element: false
        },
        {
          type: "feature",
          name: "Feature",
          icon: "🔹",
          content: "### 🔹 Feature: [Feature Name]\n```\n- Status: ✅ / 🔄 / 🚧 / 📝  \n- Owner: [Dev/PM Name]  \n- Updates:\n  - [YYYY-MM-DD] [Brief update or note]\n  - [YYYY-MM-DD] [Another update]\n- Notes\n  - Key Note 1\n  - Key Note 2\n  - Key Note 3\n\n```",
          required: true,
          element: "<button>Project</button>\n"
        },
        {
          type: "summary",
          name: "Summary",
          icon: "🔍",
          content: "## 🔍 Summary\n```\n- Progress: X%\n- Blockers: [Summary]\n- Next Milestone: [Date + Description]\n```",
          required: false,
        },
        {
          type: "timeline",
          name: "Timeline",
          icon: "🕒",
          content: "## 🕒 Timeline:\n\n ```mermaid\n gantt\n    title Project Schedule\n    dateFormat  YYYY-MM-DD\n    section Design\n    Feature A: done, 2023-01-01, 2023-01-05\n    Feature B: done, after a1, 2d\n    Feature C: done, after a2, 2d\n    section Development\n    Feature D: done, after a3, 1d\n    Feature E: done, after a4, 1d\n```",
          required: false,
        }
      ]
    },
    {
      name: "Quick Updates",
      summary: "Quick updates template",
      content: "# 📁 Topic: [Topic Name]\n**Date:** YYYY-MM-DD  \n**Participants:** [Team/Lead Name] \n---\n## 🧱 Features\n### 🔹 Feature: [Feature Name]\n```\n- Status: In progress / To-be-started / Blocked / Done  \n- Owner: [Dev/PM Name]  \n- Updates:\n  - [YYYY-MM-DD] [Brief update or note]\n  - [YYYY-MM-DD] [Another update]\n- Notes\n  - Key Note 1\n  - Key Note 2\n  - Key Note 3\n```\n\n---",
      blocks: [
        {
          type: "topic",
          name: "Topic",
          icon: "📁",
          content: "# 📁 Topic: [Topic Name]\n**Date:** YYYY-MM-DD  \n**Participants:** [Team/Lead Name] \n---\n",
          required: true,
          element: false
        },
        {
          type: "feature",
          name: "Feature",
          icon: "🔹",
          content: "### 🔹 Feature: [Feature Name]\n```\n- Status: In progress / To-be-started / Blocked / Done  \n- Owner: [Dev/PM Name]  \n- Updates:\n  - [YYYY-MM-DD] [Brief update or note]\n  - [YYYY-MM-DD] [Another update]\n- Notes\n  - Key Note 1\n  - Key Note 2\n  - Key Note 3\n\n```",
          required: true,
          element: "<button>Project</button>\n"
        },
        {
          type: "timeline",
          name: "Timeline",
          icon: "🕒",
          content: "## 🕒 Timeline:\n\n ```mermaid\n gantt\n    title Project Schedule\n    dateFormat  YYYY-MM-DD\n    section Design\n    Feature A: done, 2023-01-01, 2023-01-05\n    Feature B: done, after a1, 2d\n    Feature C: done, after a2, 2d\n    section Development\n    Feature D: done, after a3, 1d\n    Feature E: done, after a4, 1d\n```",
          required: false,
        }
      ]
    }
  ]
  
export const mermaidTemplates = [
    {
      name: "Flowchart (Top-Down)",
      description: "Simple top-down flowchart",
      code: "```mermaid\ngraph TD\n    A[Start] --> B[Process]\n    B --> C{Decision}\n    C -->|Yes| D[Result 1]\n    C -->|No| E[Result 2]\n```",
      type: "flowchart"
    },
    {
      name: "Flowchart (Left-Right)",
      description: "Horizontal flowchart",
      code: "```mermaid\ngraph LR\n    A[Start] --> B[Process]\n    B --> C{Decision}\n    C -->|Yes| D[Result 1]\n    C -->|No| E[Result 2]\n```",
      type: "flowchart"
    },
    {
      name: "Sequence Diagram",
      description: "Show sequence of interactions",
      code: "```mermaid\nsequenceDiagram\n    participant User\n    participant System\n    User->>System: Perform Action\n    System->>System: Process Action\n    System-->>User: Return Result\n```"
    },
    {
      name: "Class Diagram",
      description: "Display class structure",
      code: "```mermaid\nclassDiagram\n    class Animal {\n        +name: string\n        +eat(): void\n    }\n    class Dog {\n        +breed: string\n        +bark(): void\n    }\n    Animal <|-- Dog\n```"
    },
    {
      name: "State Diagram",
      description: "Show state transitions",
      code: "```mermaid\nstateDiagram-v2\n    [*] --> Idle\n    Idle --> Processing: Start\n    Processing --> Completed: Success\n    Processing --> Error: Failure\n    Completed --> [*]\n    Error --> Idle: Retry\n```"
    },
    {
      name: "Gantt Chart",
      description: "Project timeline visualization",
      code: "```mermaid\ngantt\n    title Project Schedule\n    dateFormat  YYYY-MM-DD\n    section Planning\n    Research           :a1, 2023-01-01, 30d\n    Design             :a2, after a1, 20d\n    section Implementation\n    Development        :a3, after a2, 40d\n    Testing            :a4, after a3, 15d\n```"
    },
    {
      name: "Pie Chart",
      description: "Simple data visualization",
      code: "```mermaid\npie\n    title Distribution\n    \"Category A\" : 40\n    \"Category B\" : 30\n    \"Category C\" : 20\n    \"Category D\" : 10\n```"
    }
  ];